import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
// Import Shared Components
import { Modal, IconButton, Loader, ErrorComponent } from 'shared-components';
import useWhiteLabeling from 'shared-components/src/hooks/useWhiteLabeling';
// Import Shared Utils
import { get } from 'shared-components/src/utils/http';
// Import Hooks
import {getCardholderSupportEmail, isHRB} from 'shared-components/src/utils/whitelabel';
import useAlert from '../../hooks/useAlert';
import usePinUnblock from '../../hooks/usePinUnblock';
import useCardReplacement, { CARD_REPLACEMENT_STATES } from '../../hooks/useCardReplacement';
import useCardActivation from '../../hooks/useCardActivation';
// Import Global Components
import Alert from '../../components/Alert';
import { PinUnblockBanner, UnblockPinModal } from '../../components/PinUnblock';
import {
  ActivateCardModal,
  CardReplacementBanner,
  ReplaceCardModal,
} from '../../components/CardReplacement';
// Import Local Components
import Transactions from './components/Transactions';
import AccountDetails from './components/AccountDetails';
import HomeHeader from './components/HomeHeader';
import CraDdaBanner from './components/CraDdaBanner';
import CraDdaEnrollModal from './components/CraDdaEnrollModal';

export default () => {
  // Set Component States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [alert, setAlert] = useAlert();
  const [cardClosedAlert, setCardClosedAlert] = useState();
  // True while the account is being re-read after a card status change
  const [refreshing, setRefreshing] = useState(false);

  // Set Data States
  const [cardholder, setCardholder] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [currentAccountPrimaryCard, setCurrentAccountPrimaryCard] = useState(
    null
  );
  const [primaryCardStatus, setPrimaryCardStatus] = useState(null);

  const [accountBalance, setAccountBalance] = useState(null);
  const [accountId, setAccountId] = useState(null);

  const intl = useIntl();
  const { callCenterBanner } = useWhiteLabeling();
  const { limitUpdateBanner } = useWhiteLabeling();

  const [limitUpdateModal, setLimitUpdateModal] = useState(false);
  const [showCraDdaModal, setShowCraDdaModal] = useState(false);

  const handleLimitUpdateClick = () => {
    setLimitUpdateModal(true);
  };


  // Fetch the cardholder and store the primary account and its primary card.
  // Returns the primary account so callers can chain further requests.
  const fetchCardholder = async () => {
    const cardholderData = await get('/api/v1/cardholder_portal/cardholder');

    setCardholder(cardholderData.data);

    // Set the currentAccount as the primary cardholder account
    const account = cardholderData.data.accounts.find(
      acc =>
        acc.processor_reference ===
        cardholderData.data.primary_processor_reference
    );
    const primaryCard = account.cards[0];
    setCurrentAccount(account);

    // NOTE: Set AccountPrimaryCard as the first card in the list
    setCurrentAccountPrimaryCard(primaryCard);
    setPrimaryCardStatus(primaryCard.status);
    // If primary card is closed, display an alert
    if (primaryCard.status === 'closed') {
      setCardClosedAlert({
        for: 'card-closed',
        type: 'info',
      });
    }

    return account;
  };

  const fetchBalance = async id => {
    const balanceData = await get(
      `/api/v1/cardholder_portal/cardholder/accounts/${id}/balance`
    );
    setAccountBalance(balanceData.data);
  };

  // Re-read the cardholder after a card status change (suspend/unsuspend) so the
  // Account Status widget and the status button reflect the new state without
  // requiring navigation away from the dashboard.
  const handleStatusChange = async () => {
    setRefreshing(true);
    try {
      await fetchCardholder();
    } catch (e) {
      // The status change itself succeeded; a failed refresh should not
      // replace the dashboard with an error screen.
    }
    setRefreshing(false);
  };

  // After a replacement order the processor has also charged its fee, so the balance is
  // re-read with the cardholder.
  const handleReplaced = async () => {
    setRefreshing(true);
    try {
      const account = await fetchCardholder();
      await fetchBalance(account.id);
    } catch (e) {
      // The order itself succeeded; a failed refresh should not replace the dashboard
      // with an error screen.
    }
    setRefreshing(false);
  };

  // Self-service PIN unlock for the primary account; the banner renders here and the
  // button in the header, so both share one state. An unlock can lift the card's
  // suspension, so the account is re-read like any other status change.
  const pinUnblock = usePinUnblock(currentAccount, {
    onUnblocked: handleStatusChange,
  });

  // Self-serve card replacement for the primary account; the button sits in the header and
  // the banner and dialog here, over one state. The order blocks the old card and takes the
  // fee, so the account and its balance are re-read once the cardholder leaves the dialog.
  const cardReplacement = useCardReplacement(currentAccount, {
    onReplaced: handleReplaced,
  });

  // Activating the replacement card from the banner, in a dialog; activation ends the
  // replacement, so the cardholder is re-read once the dialog closes.
  const cardActivation = useCardActivation({ onActivated: handleStatusChange });

  // Set Effects
  useEffect(() => {
    const fetchData = async () => {
      setError(false);

      try {
        const account = await fetchCardholder();

        // Set accountId here
        setAccountId(account.id);

        // Fetch Account Balance
        await fetchBalance(account.id);
      } catch (e) {
        setError(true);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  // Full-screen overlay shown while the account is re-read after a status change
  const refreshingOverlay = refreshing && (
    <Modal>
      <Loader />
    </Modal>
  );

  const homeHeaderData = {
    lastFourDigits: currentAccountPrimaryCard
      ? currentAccountPrimaryCard.last_four_digits
      : null,
    user: {
      firstName: cardholder.first_name,
      lastName: cardholder.last_name,
    },
    accountId: currentAccount.id,
    cardStatus: primaryCardStatus,
    account: currentAccount,
  };

  return (
    <>
      {refreshingOverlay}
      { 
        callCenterBanner && (
          <Alert
            // eslint-disable-next-line dot-notation
            alert={intl.messages['cardholder_support_email']}
            type="warn"
            alertFor="call-center-banner"
            values={{
              cardholder_support_email: getCardholderSupportEmail()
            }}
          />
        ) 
      }
      {
        limitUpdateBanner && isHRB() && (
          <Alert
            // eslint-disable-next-line dot-notation
            alert={intl.messages['program-update-short-alert']}
            type='warn'
            alertFor='program-update-short-alert'
            style={{
              display: 'inline',
            }}
            values={{
              a: chunks => (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLimitUpdateClick();
                  }}
                  style={{
                    textDecoration: 'underline',
                    color: 'inherit',
                  }}
                >
                  {chunks}
                </button>
              ),
              strong: chunks => (
                <strong>
                  {chunks}
                </strong>
              ),
            }}
          />
        )
      }
      {
        limitUpdateModal && isHRB() && (
          <Modal>
            <IconButton
              iconType='x'
              onClick={() => setLimitUpdateModal(false)}
              style={{
                position: 'absolute',
                top: '30px',
                right: '30px'
              }}
            />
            <div style={{ padding: '20px', maxWidth: '600px' }}>
              <h2><strong>{intl.messages['program-update-header']}</strong></h2>
              <br />
              <p>{intl.messages['program-update-body1']}<strong>{intl.messages['program-update-body1-1']}</strong></p>
              <br />
              <h4><strong>{intl.messages['transaction-limits-update-header']}</strong></h4>
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-body1']}</th>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-current']}</th>
                    <th style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-update-new']}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px'}}>{intl.messages['transaction-limits-max-load-amount-per-transaction']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-load-amount-per-transaction-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-load-amount-per-transaction-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-card-balance-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-pos-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-spend-atm-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-bill-pay-per-txn-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-out-funds-transfer-amt-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-load-amt-external-account-new']}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit-current']}</td>
                    <td style={{ border: '1px solid black', padding: '5px' }}>{intl.messages['transaction-limits-max-daily-direct-deposit-new']}</td>
                  </tr>
                </tbody>
              </table>
              <br />
              <p>
                {intl.messages['cardholder-agreement-website-1']} <a href="https://hrblockadvantagecard.payaccount.io/">website</a> {intl.messages['cardholder-agreement-website-2']}
              </p>
              <br />
              <p>
                {intl.messages['no-action-needed']}
              </p>
              <br />
              <p>
                {intl.messages['disagreement-option-1']}<strong>1-855-342-6924.</strong>{intl.messages['disagreement-option-2']}
              </p>
              <br />
              <p>
                {intl.messages['program-issuer-1']}
              </p>
              <br />
              <p>{intl.messages['program-mastercard-trademark']}</p>
              <br />
              <p>{intl.messages['interac-trademark']}</p>
            </div>
          </Modal>
        )
      }
      {alert && (
        <Alert
          alert={intl.messages[`${alert.for}-alert`]}
          alertFor={alert.for}
          type={alert.type}
        />
      )}
      {/* Above the header, as in the mockup: the first thing seen while a card is on the way */}
      <CardReplacementBanner
        state={cardReplacement.state}
        requestedAt={cardReplacement.requestedAt}
        previousLastFour={cardReplacement.previousLastFour}
        onActivate={cardActivation.open}
      />
      <HomeHeader
        data={homeHeaderData}
        setAlert={setAlert}
        onStatusChange={handleStatusChange}
        pinUnblock={pinUnblock}
        cardReplacement={cardReplacement}
      />
      {cardClosedAlert && (
        <Alert
          alert={intl.messages[`${cardClosedAlert.for}-alert`]}
          alertFor={cardClosedAlert.for}
          type={cardClosedAlert.type}
        />
      )}
      <PinUnblockBanner
        state={pinUnblock.state}
        nextAvailableAt={pinUnblock.nextAvailableAt}
      />
      <UnblockPinModal
        state={pinUnblock.state}
        submitting={pinUnblock.submitting}
        failed={pinUnblock.failed}
        onConfirm={pinUnblock.confirm}
        onCancel={pinUnblock.cancel}
      />
      <ReplaceCardModal
        isOpen={cardReplacement.isOpen}
        phase={cardReplacement.phase}
        quote={cardReplacement.quote}
        balanceCents={cardReplacement.balanceCents}
        mailingAddress={cardholder}
        passwordError={cardReplacement.passwordError}
        onPasswordChange={cardReplacement.clearPasswordError}
        onSubmit={cardReplacement.submit}
        onRetry={cardReplacement.retry}
        onClose={cardReplacement.close}
      />
      <ActivateCardModal
        isOpen={cardActivation.isOpen}
        phase={cardActivation.phase}
        card={cardActivation.card}
        errors={cardActivation.errors}
        consents={cardActivation.consents}
        formError={cardActivation.formError}
        onChange={cardActivation.change}
        onBlur={cardActivation.blur}
        onConsent={cardActivation.consent}
        onSubmit={cardActivation.submit}
        onClose={cardActivation.close}
      />
      <AccountDetails
        accountId={accountId}
        account={currentAccount}
        // The old card is blocked from the order until the new one is activated
        status={
          cardReplacement.state === CARD_REPLACEMENT_STATES.IN_PROGRESS
            ? 'not_active'
            : currentAccount.status_code
        }
        expiryDate={currentAccountPrimaryCard.expiry_date}
        accountBalance={accountBalance}
      />
      {accountId && (
        <CraDdaBanner
          accountId={accountId}
          onEnroll={() => setShowCraDdaModal(true)}
        />
      )}
      {showCraDdaModal && (
        <CraDdaEnrollModal
          accountId={accountId}
          cardholder={cardholder}
          account={currentAccount}
          onClose={() => setShowCraDdaModal(false)}
        />
      )}
      <Transactions accountId={accountId} accountBalance={accountBalance} />{' '}
    </>
  );
};
